// Copied from kettle-platform workers/admin/ui/src/charts/scale.ts (2026-10-30); restyled for Estra, see docs/kettle-sharing.md.
// Shared chart maths: nice axis maxima, null-broken runs and monotone curves, short dates.
export type Pt = [number, number];

/** The next "nice" axis maximum at or above v (1, 1.5, 2, 2.5, 3, 4, 5, 6, 8 × 10ⁿ). */
export function niceMax(v: number) {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].map((m) => m * p).find((m) => m >= v)!;
}

/** Runs of consecutive non-null points; a null breaks the line. */
export function runs(vals: (number | null)[], x: (i: number) => number, y: (v: number) => number): Pt[][] {
  const out: Pt[][] = [];
  let cur: Pt[] = [];
  vals.forEach((v, i) => {
    if (v === null) {
      if (cur.length) out.push(cur);
      cur = [];
    } else cur.push([x(i), y(v)]);
  });
  if (cur.length) out.push(cur);
  return out;
}

/** Monotone cubic (Fritsch–Carlson, as d3's curveMonotoneX): smooth, with no overshoot. */
export function smooth(pts: Pt[]): string {
  const f = (n: number) => n.toFixed(1);
  if (pts.length < 3) return pts.map(([px, py], i) => `${i ? "L" : "M"}${f(px)} ${f(py)}`).join("");
  const s = pts.slice(1).map(([px, py], i) => (py - pts[i]![1]) / (px - pts[i]![0]));
  const m = pts.map((_, i) => {
    if (i === 0 || i === pts.length - 1) return 0;
    const a = s[i - 1]!, b = s[i]!;
    return a * b <= 0 ? 0 : (Math.sign(a) + Math.sign(b)) * Math.min(Math.abs(a), Math.abs(b), 0.25 * Math.abs(a + b));
  });
  m[0] = (3 * s[0]! - m[1]!) / 2;
  m[pts.length - 1] = (3 * s[s.length - 1]! - m[pts.length - 2]!) / 2;
  // Keep the end tangents from overshooting too.
  if (m[0]! * s[0]! <= 0) m[0] = 0;
  else if (Math.abs(m[0]!) > 3 * Math.abs(s[0]!)) m[0] = 3 * s[0]!;
  const e = pts.length - 1;
  if (m[e]! * s[e - 1]! <= 0) m[e] = 0;
  else if (Math.abs(m[e]!) > 3 * Math.abs(s[e - 1]!)) m[e] = 3 * s[e - 1]!;
  let d = `M${f(pts[0]![0])} ${f(pts[0]![1])}`;
  for (let i = 0; i < e; i++) {
    const [x0, y0] = pts[i]!, [x1, y1] = pts[i + 1]!;
    const dx = (x1 - x0) / 3;
    d += `C${f(x0 + dx)} ${f(y0 + dx * m[i]!)} ${f(x1 - dx)} ${f(y1 - dx * m[i + 1]!)} ${f(x1)} ${f(y1)}`;
  }
  return d;
}

/** "Oct 27" from "2026-10-27". */
export const shortDay = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
